// components/PriorityQueue.jsx
import React from 'react';
import { Loader2, X } from 'lucide-react';
import styles from '../AIControlRoomAdaptive.module.css';
import usePriorityQueue from '../hooks/usePriorityQueue';

const PriorityQueue = ({ selectedTenant, selectedCampaign = 'all', selectedDateRange = 'last7', channel = 'sms', isGlobalAdmin = false }) => {
  const { queue, loading, error, dismissLead } = usePriorityQueue(50, selectedTenant, selectedDateRange, channel, selectedCampaign);

  const urgentCount = queue.filter(item => item.urgent).length;

  const handleLeadClick = (leadId) => {
    window.location.href = `/lead/${leadId}`;
  };

  const handleDismiss = (e, leadId) => {
    e.stopPropagation();
    dismissLead(leadId);
  };

  const truncateSnippet = (text, maxLength = 60) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  if (loading) {
    return (
      <div className={styles.leftPanel}>
        <div className={styles.panelHeader}>
          <div className={styles.panelTitle}>Priority Queue</div>
        </div>
        <div className={styles.queueList}>
          <div className={styles.loadingState}>
            <Loader2 className={styles.spinner} size={20} />
            <span>Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.leftPanel}>
      <div className={styles.panelHeader}>
        <div className={styles.panelTitle}>
          Priority Queue
          {urgentCount > 0 && (
            <span className={styles.priorityBadge}>{urgentCount}</span>
          )}
        </div>
      </div>

      <div className={styles.queueList}>
        {queue.length === 0 ? (
          <div className={styles.emptyState}>No hot leads waiting</div>
        ) : (
          queue.map(item => (
            <div
              key={item.id}
              className={`${styles.queueItem} ${item.urgent ? styles.urgent : ''} ${item.isNew ? styles.newLead : ''}`}
              onClick={() => handleLeadClick(item.id)}
              style={{ cursor: 'pointer' }}
            >
              <button
                type="button"
                className={styles.dismissButton}
                onClick={(e) => handleDismiss(e, item.id)}
                title="Remove from queue"
                aria-label={`Remove ${item.name} from priority queue`}
              >
                <X size={14} />
              </button>

              <div className={styles.itemHeader}>
                <div className={styles.itemName}>
                  {item.awaitingReply && (
                    <span
                      className={styles.replyDot}
                      title="Replied after handoff — needs a human response"
                      aria-label="Replied after handoff, needs a human response"
                    />
                  )}
                  {item.name}
                </div>
                <div className={styles.itemTime}>{item.time}</div>
              </div>

              {item.label && (
                <span className={styles.kindTag}>{item.label}</span>
              )}

              {/* Compound state: a hot/booked handoff that ALSO has an open info request.
                  The primary tag can only show one reason, so surface the info ask separately.
                  A pure info request already reads "Info request" in the label, so skip it there. */}
              {item.infoRequested && item.kind !== 'info_request' && (
                <span
                  title={item.infoEmail ? `Also wants info sent to ${item.infoEmail}` : 'Also wants info sent'}
                  style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: '#fef9c3', color: '#854d0e', whiteSpace: 'nowrap' }}
                >
                  📧 also wants info
                </span>
              )}

              <div className={styles.itemSnippet}>
                {truncateSnippet(item.snippet)}
              </div>

              <div className={styles.itemFooter}>
                {item.business && (
                  <div className={styles.itemBusiness}>{item.business}</div>
                )}
                {isGlobalAdmin && item.campaignName && (
                  <div className={styles.itemCampaign}>{item.campaignName}</div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PriorityQueue;